<template>
  <div data-dynatrace="product-price" data-test-id="product-pricing">
    <span
      class="gap-x-2 i-flex wrap "
      :class="[classWrapper, { 'f-col gap-y-0.5': block }]"
    >
      <template v-if="isRange && !showDiscountedRanges">
        <span :class="[classCurrent, { '': price.highCurrent < price.lowOriginal && !suppressSaleStyling }]">
          {{ current }}
        </span>
      </template>
      <template v-else>
        <component
          :is="!isVisuallyDiscounted ? 'span' : 'del'"
          v-if="(!isVisuallyDiscounted || !hideStrikethroughPrice) && !suppressSaleStyling"
          v-style:c="originalPriceColor"
          :class="!isVisuallyDiscounted ? classCurrent : brandClasses.original"
        >
          <span v-if="isDiscounted" class="sr-only">
            {{ isOmnibus ? $t.previousDiscountedPrice : $t.originalPrice }}:
          </span>
          {{ isOmnibus ? historicBest : original }}
        </component>
        <del v-if="price.previousBestSalesPrice && !suppressSaleStyling" v-style:c="salePriceColor" :class="[classCurrent, brandClasses.sale]">
          <span class="sr-only">{{ $t.previousDiscountedPrice }}:</span>
          {{ useFormattedPrice(price.previousBestSalesPrice, currency) }}
        </del>
        <ins v-if="isVisuallyDiscounted" v-style:c="salePriceColor" :class="[classCurrent, brandClasses.sale]" style="text-decoration: none">
          <span class="sr-only">
            {{ price.previousBestSalesPrice || isOmnibus ? $t.currentDiscountedPrice : $t.discountedPrice }}:
          </span>
          <component :is="block ? 'div' : 'span'" class="ws-nowrap">
            {{ current }}
            <template v-if="showDiscountPercentage && !isRange">
              ({{ discountPercentage }})
            </template>
          </component>
        </ins>
        <span v-if="suppressSaleStyling" :class="classCurrent">
          {{ current }}
        </span>
      </template>
      <vf-tooltip
        v-if="showSalePriceTooltip && price.salesPriceActiveDate"
        v-style:c="suppressSaleStyling ? undefined : salePriceColor"
        class="-ml-2"
        :class="suppressSaleStyling ? '' : brandClasses.sale"
      >
        <template #trigger>
          <vf-icon name="info" />
        </template>
        {{
          replaceAll(
            $t.salesPriceActiveDateInfo,
            { date: formatDate(new Date(price.salesPriceActiveDate), 'MMMM YYYY') },
          )
        }}
      </vf-tooltip>
    </span>
    <div
      v-if="isOmnibus && showOriginalPriceForOmnibus"
      class="mt-2 w-fit flex gap-x-2 bg-grey-80 p-2 text-xs c-grey-10 "
    >
      {{ $t.originalPrice }}: {{ original }}
      <vf-toggletip placement="top-start">
        <template #trigger>
          <vf-icon name="info" />
        </template>
        {{ $t.omnibusTooltip }}
      </vf-toggletip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { CSSClass } from '#types/common'
import type { PriceFull } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { formatDate } from '@vueuse/core'

const { currency, price } = defineProps<{
  /**
   * Price to display
   * Example of strike through sales price range and current price range.
   *
   * $20-$40 - Original Price range: - strike through
   * $10-$30 - Current Range.
   * $20 - lowOriginal
   * $40 - highOriginal
   * $10 - lowCurrent
   * $30 - highCurrent
   */
  price: PriceFull
  /**
   * Currency of the price
   */
  currency: string
  /**
   * Classes to be applied to the current price
   */
  classCurrent?: CSSClass
  /**
   * Classes to be applied to the wrapper element
   */
  classWrapper?: CSSClass
  /**
   * Sets displaying the discount amount as percentage
   */
  showDiscountPercentage?: boolean
  /**
   * Show or hide the omnibus details, use for drawer interaction
   * Default: true
   */
  showOmnibusDetails?: boolean
  /**
   * Shows box with original price and omnibus tooltip below sale price.
   * Currently only shown on PDP and Quickshop
   */
  showOriginalPriceForOmnibus?: boolean
  /**
   * Show original price and discounted price below it
   * Currently only use on order Details
   */
  block?: boolean
  compactOmnibus?: boolean
  hideStrikethroughPrice?: boolean
  isDigitalProduct?: boolean
  showSalePriceTooltip?: boolean
}>()

const { brandClasses, showDiscountedRanges } = useAppConfig().components.product.pricing
const { $feature, $t } = useNuxtApp()

const formatPrice = (low: number, high: number) => {
  if (low === high)
    return low === 0 ? $t.free : useFormattedPrice(low, currency)
  return `${useFormattedPrice(low, currency)} - ${useFormattedPrice(high, currency)}`
}

const original = computed(() => formatPrice(price.lowOriginal, price.highOriginal))

const current = computed(() => formatPrice(price.lowCurrent, price.highCurrent))

const historicBest = computed(() => useFormattedPrice(price.historicBestPrice ?? 0, currency))

const isDiscounted = computed(() => current.value !== original.value)

const isOmnibus = computed(() => {
  const { historicBestPrice, lowCurrent, lowOriginal } = price
  return (
    $feature.showOmnibusPricing
    && historicBestPrice !== undefined
    && lowCurrent < lowOriginal
    && lowCurrent < historicBestPrice
  )
})

const isRange = computed(() => price.lowCurrent !== price.highCurrent)

const discountPercentage = computed(() => {
  const { highOriginal, lowCurrent, historicBestPrice } = price
  const percentage = isOmnibus.value
    ? getPercentage(historicBestPrice!, historicBestPrice! - lowCurrent, 0)
    : getPercentage(highOriginal, highOriginal - lowCurrent, 0)
  return `${$feature.showNegativeDiscountPercentageOnCatalog ? '-' : ''}${percentage}%`
})

// If Omnibus info is available but current price is not below the 30-day lowest,
// suppress sale/red styling and treat price as non-discounted for display.
const suppressSaleStyling = computed(
  () => $feature.showOmnibusPricing && price.historicBestPrice && price.lowCurrent >= price.historicBestPrice
)

const isVisuallyDiscounted = computed(() => isDiscounted.value && !suppressSaleStyling.value)

const { originalPriceColor, salePriceColor } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)
</script>
